/**
 * Decode over-escaped HTML entities in quiz copy (e.g. &amp;#x27; from double-escaping).
 */
/**
 * Normalize API quiz review fields (string, number, or legacy object-shaped answers) for display.
 */
export function formatQuizReviewField(raw) {
    if (raw == null)
        return '';
    if (typeof raw === 'string')
        return decodeQuizHtmlEntities(raw);
    if (typeof raw === 'number' || typeof raw === 'boolean')
        return decodeQuizHtmlEntities(String(raw));
    if (typeof raw === 'object') {
        const o = raw;
        if (typeof o.answer === 'string')
            return decodeQuizHtmlEntities(o.answer);
        if (typeof o.text === 'string')
            return decodeQuizHtmlEntities(o.text);
        if (typeof o.response === 'string')
            return decodeQuizHtmlEntities(o.response);
        try {
            return JSON.stringify(raw);
        }
        catch {
            return '';
        }
    }
    return decodeQuizHtmlEntities(String(raw));
}
/**
 * Entities produced by `validator.escape()` on the API boundary, plus their
 * decimal equivalents. `&amp;` must be decoded LAST on each pass so that
 * `&amp;#x2F;` collapses one layer at a time rather than short-circuiting.
 */
const QUIZ_ENTITY_REPLACEMENTS = [
    [/&quot;/gi, '"'],
    [/&#0*34;/g, '"'],
    [/&#x0*22;/gi, '"'],
    [/&apos;/gi, "'"],
    [/&#0*39;/g, "'"],
    [/&#x0*27;/gi, "'"],
    [/&lt;/gi, '<'],
    [/&#0*60;/g, '<'],
    [/&#x0*3C;/gi, '<'],
    [/&gt;/gi, '>'],
    [/&#0*62;/g, '>'],
    [/&#x0*3E;/gi, '>'],
    // Forward slash — the one that mangles every algebraic fraction (`6/x`).
    [/&#x0*2F;/gi, '/'],
    [/&#0*47;/g, '/'],
    [/&#x0*5C;/gi, '\\'],
    [/&#0*92;/g, '\\'],
    [/&#0*96;/g, '`'],
    [/&#x0*60;/gi, '`'],
    [/&nbsp;/gi, ' '],
    // Always last: decoding `&` first would merge separate layers.
    [/&amp;/gi, '&'],
];
/**
 * Collapse HTML entities that the API's input sanitizer baked into stored quiz
 * text. Historic rows can carry several layers (`&amp;amp;#x2F;`), so this
 * loops until the string stops changing.
 */
export function decodeQuizHtmlEntities(raw) {
    if (raw == null || typeof raw !== 'string')
        return '';
    let s = raw;
    for (let i = 0; i < 8; i += 1) {
        let next = s;
        for (const [pattern, replacement] of QUIZ_ENTITY_REPLACEMENTS) {
            next = next.replace(pattern, replacement);
        }
        if (next === s)
            break;
        s = next;
    }
    return s;
}
/**
 * Avoid "A. A) ..." when option text already includes a leading letter prefix from the generator.
 */
export function formatMultipleChoiceOptionLabel(letter, raw) {
    const t = (raw ?? '').trim();
    if (!t)
        return '';
    const re = new RegExp(`^${letter}\\s*[\\).]\\s*`, 'i');
    if (re.test(t))
        return t.replace(re, '').trim();
    return t;
}
/**
 * Resolve which multiple-choice option is correct.
 *
 * `correctAnswer` is normally a letter (A–D) but generators and imports also
 * emit a 1-based index or the full option text, so all three are accepted.
 * Comparison is done on decoded text — a stored answer and its option can
 * carry different amounts of entity escaping.
 *
 * Returns the 0-based option index, or -1 when it cannot be resolved.
 */
export function resolveMultipleChoiceCorrectIndex(correctAnswer, options) {
    if (correctAnswer == null)
        return -1;
    const raw = decodeQuizHtmlEntities(String(correctAnswer)).trim();
    if (!raw)
        return -1;
    const letter = raw.match(/^([A-Z])[\s).:]*$/i);
    if (letter) {
        const idx = letter[1].toUpperCase().charCodeAt(0) - 65;
        return idx >= 0 && idx < options.length ? idx : -1;
    }
    const num = raw.match(/^([1-9]\d*)$/);
    if (num) {
        const idx = Number.parseInt(num[1], 10) - 1;
        return idx >= 0 && idx < options.length ? idx : -1;
    }
    const target = raw.toLowerCase();
    return options.findIndex((opt) => {
        const text = decodeQuizHtmlEntities(opt ?? '').trim().toLowerCase();
        return text.length > 0 && text === target;
    });
}
